import { Building2, FileCheck2, FileText, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Painel } from '@social/shared';
import { CartaoKpi } from '../componentes/cartao-kpi';
import { PaginaShell } from '../componentes/pagina-shell';
import { TelaCarregando } from '../componentes/carregando';
import { api, useSessao } from '../lib/sessao';
import { Agenda, Atalhos, Bloco, UltimasSociedades, UltimosAtos } from './inicio-partes';

/**
 * A TELA INICIAL — onde o contador cai ao entrar.
 *
 * ## A pergunta que ela responde
 * "O que eu faço agora?" Nessa ordem: o que está atrasado (agenda), o que eu
 * posso começar (atalhos), onde eu parei (últimos registros). Os números do
 * topo são contexto, não conteúdo — por isso são quatro e ficam numa linha.
 *
 * ## Por que a agenda vem antes dos atalhos
 * Porque exigência da Junta tem cliente esperando do outro lado, e atalho não.
 * Numa tela inicial, o que está acima da dobra é o que o produto diz ser
 * importante — e um escritório com uma exigência aberta não deveria ver
 * "Nova sociedade" primeiro.
 *
 * ## Uma requisição
 * Tudo vem de `GET /painel`. Ver o comentário de `Painel` no domínio para o
 * porquê de não serem seis chamadas.
 */
export function Inicio(): JSX.Element {
  const eu = useSessao((s) => s.eu);
  const navegar = useNavigate();
  const painel = useQuery({
    queryKey: ['painel'],
    queryFn: () => api.get<Painel>('/painel'),
  });

  if (painel.isPending) return <TelaCarregando />;

  if (painel.isError || !painel.data) {
    return (
      <PaginaShell titulo="Início">
        <p role="alert" className="text-erro-600">
          Não foi possível carregar o painel. Recarregue a página.
        </p>
      </PaginaShell>
    );
  }

  const { resumo, agenda, agendaOculta, ultimosAtos, ultimasSociedades } = painel.data;
  const primeiroNome = eu?.nome.trim().split(/\s+/)[0] ?? '';

  /*
    Sem nenhuma sociedade, o escritório é novo: os cartões diriam "0" quatro
    vezes, e a agenda estaria vazia com razão. A tela vira uma coisa só — o
    convite para começar. Ver "proporção" no cabeçalho do `PaginaShell`.
  */
  const escritorioNovo = resumo.sociedades === 0;

  return (
    <PaginaShell
      titulo={primeiroNome ? `Bom trabalho, ${primeiroNome}` : 'Início'}
      subtitulo={
        escritorioNovo
          ? 'Seu escritório está pronto. Comece cadastrando a primeira sociedade.'
          : 'O que precisa de você, e por onde continuar.'
      }
      /*
        SEM grid próprio aqui: o `PaginaShell` já envolve este slot num
        `grid-cols-2 xl:grid-cols-4`. Um grid aninhado vira UMA célula dele, e
        os quatro cartões se espremem numa coluna — os rótulos e os números
        colapsam para largura zero e sobra só o ícone. O teste não pegava: o
        texto continuava no DOM, invisível.
      */
      indicadores={
        escritorioNovo ? undefined : (
          <>
            {/*
              Os dois primeiros são CLICÁVEIS porque existe para onde ir — e é
              o clique que o `CartaoKpi` transforma em reação de hover (o
              cartão sobe e ganha sombra). Atos e documentos ainda não têm
              tela de lista própria: dar-lhes hover de clicável prometeria um
              destino que não existe.

              Rótulo curto: no celular a grade é de duas colunas, e "Sócios e
              administradores" saía "Sócios e admini…" — medido no navegador,
              não estimado. O que explica desceu para o `detalhe`, que quebra
              em vez de cortar no estreito (ver `CartaoKpi`).
            */}
            <CartaoKpi
              rotulo="Sociedades"
              valor={String(resumo.sociedades)}
              detalhe="na carteira"
              icone={Building2}
              tom="marca"
              aoClicar={() => void navegar('/sociedades')}
            />
            <CartaoKpi
              rotulo="Pessoas"
              valor={String(resumo.pessoas)}
              detalhe="sócios e administradores"
              icone={Users}
              aoClicar={() => void navegar('/pessoas')}
            />
            <CartaoKpi
              rotulo="Atos no mês"
              valor={String(resumo.atosNoMes)}
              detalhe="com data neste mês"
              icone={FileText}
            />
            <CartaoKpi
              rotulo="Documentos"
              valor={String(resumo.documentosGerados)}
              detalhe="gerados até aqui"
              icone={FileCheck2}
              tom="ok"
            />
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {!escritorioNovo && (
          <section className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-tinta-900 font-semibold">Precisa de você</h2>
              {agenda.length > 0 && (
                <span className="text-tinta-500 text-sm tabular-nums">
                  {agenda.length} {agenda.length === 1 ? 'item' : 'itens'}
                  {/*
                    O teto por fonte é de 5 (ver `TETO_POR_FONTE`), e o corte
                    tem de ser DITO: quem tem quarenta exigências veria cinco e
                    concluiria que tem cinco.
                  */}
                  {agendaOculta > 0 && ` · e mais ${String(agendaOculta)}`}
                </span>
              )}
            </div>
            <Agenda itens={agenda} />
          </section>
        )}

        <Atalhos />

        {!escritorioNovo && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Bloco titulo="Onde você parou">
              <UltimosAtos atos={ultimosAtos} />
            </Bloco>
            <Bloco
              titulo="Últimas sociedades"
              acao={{ para: '/sociedades', rotulo: 'Ver todas' }}
            >
              <UltimasSociedades sociedades={ultimasSociedades} />
            </Bloco>
          </div>
        )}
      </div>
    </PaginaShell>
  );
}
